import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  ChevronRight,
  Search,
  SlidersHorizontal,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import {
  Badge,
  CustomerStatusBadge,
  EmptyState,
  PageHeader,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '../ui';
import { customersService } from '../../services/customers.service';

/* ── helpers ────────────────────────────────────────────────────────────────── */
export const avatarInitials = (first, last, email) => {
  if (first && last) return `${first[0]}${last[0]}`.toUpperCase();
  if (first) return first.slice(0, 2).toUpperCase();
  if (email) return email.slice(0, 2).toUpperCase();
  return '??';
};

const relativeTime = (iso) => {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
};

const AVATAR_COLORS = [
  'bg-accent-100 text-accent-700',
  'bg-success-100 text-success-700',
  'bg-info-100 text-info-700',
  'bg-warning-100 text-warning-800',
  'bg-danger-100 text-danger-700',
];
const avatarColor = (id) => AVATAR_COLORS[(id?.charCodeAt(0) ?? 0) % AVATAR_COLORS.length];

/* ── status groups for filter ────────────────────────────────────────────────── */
const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'PENDING_KYC_VERIFICATION', label: 'Pending KYC' },
  { value: 'PENDING_MANUAL_VERIFICATION', label: 'Manual review' },
  { value: 'PENDING_INITIAL_DEPOSIT', label: 'Pending deposit' },
  { value: 'KYC_VERIFICATION_FAILED', label: 'KYC failed' },
  { value: 'PENDING_TIN_SUBMISSION', label: 'TIN needed' },
  { value: 'SUSPENDED', label: 'Suspended' },
  { value: 'BLOCKED', label: 'Blocked' },
];

const QUICK_FILTERS = [
  { label: 'Active', status: 'ACTIVE', tone: 'success' },
  { label: 'Pending KYC', status: 'PENDING_KYC_VERIFICATION', tone: 'warning' },
  { label: 'Manual review', status: 'PENDING_MANUAL_VERIFICATION', tone: 'info' },
  { label: 'KYC failed', status: 'KYC_VERIFICATION_FAILED', tone: 'danger' },
  { label: 'Suspended', status: 'SUSPENDED', tone: 'danger' },
];

/* ── row skeleton ───────────────────────────────────────────────────────────── */
const RowSkeleton = () => (
  <TR>
    <TD>
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 rounded-full bg-ink-100 animate-pulse shrink-0" />
        <div className="space-y-1.5">
          <div className="h-3 w-32 bg-ink-100 animate-pulse rounded" />
          <div className="h-2.5 w-24 bg-ink-100 animate-pulse rounded" />
        </div>
      </div>
    </TD>
    <TD><div className="h-3 w-24 bg-ink-100 animate-pulse rounded" /></TD>
    <TD><div className="h-5 w-20 bg-ink-100 animate-pulse rounded" /></TD>
    <TD><div className="h-3 w-20 bg-ink-100 animate-pulse rounded" /></TD>
    <TD><div className="h-3 w-16 bg-ink-100 animate-pulse rounded" /></TD>
    <TD />
  </TR>
);

/* ── component ──────────────────────────────────────────────────────────────── */
const CustomersList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [total, setTotal] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');

  const debounceRef = useRef(null);

  /* sync URL params */
  useEffect(() => {
    const p = {};
    if (search) p.search = search;
    if (statusFilter) p.status = statusFilter;
    setSearchParams(p, { replace: true });
  }, [search, statusFilter, setSearchParams]);

  const load = useCallback(async (cursor = null) => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: 50 };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (cursor) params.cursor = cursor;

      const data = await customersService.listCustomers(params);
      const rows = data?.users ?? data ?? [];
      const nc = data?.nextCursor ?? null;
      const tot = data?.total ?? null;

      setCustomers((prev) => (cursor ? [...prev, ...rows] : rows));
      setNextCursor(nc);
      setTotal(tot);
      setHasMore(!!nc);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load customers.');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  /* reload: immediate when no search, debounced when typing */
  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!search) {
      load(null);
    } else {
      debounceRef.current = setTimeout(() => load(null), 350);
    }
    return () => clearTimeout(debounceRef.current);
  }, [search, statusFilter, load]);

  const handleSearchChange = (e) => setSearch(e.target.value);
  const handleStatusChange = (val) => { setStatusFilter(val); setShowFilters(false); };
  const handleClearFilters = () => { setSearch(''); setStatusFilter(''); };

  const activeFilterCount = (search ? 1 : 0) + (statusFilter ? 1 : 0);

  return (
    <div>
      <PageHeader
        title="Customers"
        description="All customers registered under your organisation."
        actions={
          total != null && (
            <span className="text-sm text-ink-500 tnum">
              {total.toLocaleString('en-US')} total
            </span>
          )
        }
      />

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ink-400 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={handleSearchChange}
            placeholder="Search by name, email, or reference…"
            className="input pl-8 text-sm h-9 w-full"
            data-testid="search-input"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-700"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Filter button */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowFilters((o) => !o)}
            className={`btn btn-sm btn-secondary gap-1.5 ${activeFilterCount > 0 ? 'border-accent-300 text-accent-700 bg-accent-50' : ''}`}
          >
            <SlidersHorizontal className="h-3.5 w-3.5" />
            Filters
            {activeFilterCount > 0 && (
              <span className="ml-0.5 h-4 w-4 rounded-full bg-accent-600 text-white text-2xs flex items-center justify-center font-bold">
                {activeFilterCount}
              </span>
            )}
          </button>

          {showFilters && (
            <div className="absolute top-full mt-1 left-0 z-20 w-52 bg-white rounded-xl shadow-elevated border border-ink-200 py-1">
              <p className="text-2xs font-semibold uppercase tracking-wider text-ink-400 px-3 pt-2 pb-1">Account status</p>
              {STATUS_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handleStatusChange(opt.value)}
                  className={`w-full text-left px-3 py-1.5 text-sm flex items-center justify-between transition-colors
                    ${statusFilter === opt.value
                      ? 'bg-accent-50 text-accent-700 font-medium'
                      : 'text-ink-700 hover:bg-ink-50'
                    }`}
                >
                  {opt.label}
                  {statusFilter === opt.value && (
                    <span className="h-1.5 w-1.5 rounded-full bg-accent-600" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Clear */}
        {activeFilterCount > 0 && (
          <button
            type="button"
            onClick={handleClearFilters}
            className="text-xs text-ink-500 hover:text-ink-800 flex items-center gap-1"
          >
            <X className="h-3 w-3" /> Clear
          </button>
        )}

        {/* Quick filter pills */}
        <div className="flex items-center gap-1.5 ml-auto flex-wrap">
          {QUICK_FILTERS.map((qf) => (
            <button
              key={qf.status}
              type="button"
              onClick={() => setStatusFilter((s) => s === qf.status ? '' : qf.status)}
              className={`badge cursor-pointer transition-colors text-xs
                ${statusFilter === qf.status
                  ? qf.tone === 'success' ? 'bg-success-100 text-success-800 border-success-200'
                    : qf.tone === 'warning' ? 'bg-warning-100 text-warning-800 border-warning-200'
                    : qf.tone === 'info' ? 'bg-info-100 text-info-800 border-info-200'
                    : 'bg-danger-100 text-danger-800 border-danger-200'
                  : 'bg-ink-50 text-ink-500 border-ink-200 hover:bg-ink-100'
                }`}
            >
              {qf.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table card */}
      <div className="bg-white border border-ink-200 rounded-xl shadow-card overflow-hidden">
        {error ? (
          <div className="flex flex-col items-center gap-3 py-12 text-center">
            <p className="text-sm text-danger-600">{error}</p>
            <button type="button" onClick={() => load(null)} className="btn btn-sm btn-secondary">Retry</button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="table w-full">
                <THead>
                  <TR>
                    <TH className="w-64">Customer</TH>
                    <TH className="w-36">Phone</TH>
                    <TH className="w-36">Status</TH>
                    <TH className="w-36">Reference</TH>
                    <TH className="w-28">Joined</TH>
                    <TH className="w-6" />
                  </TR>
                </THead>
                <TBody>
                  {loading && customers.length === 0
                    ? Array.from({ length: 8 }).map((_, i) => <RowSkeleton key={i} />)
                    : customers.length === 0
                    ? null
                    : customers.map((c) => {
                        const initials = avatarInitials(c.firstName, c.lastName, c.email);
                        const color = avatarColor(c.customerId);
                        return (
                          <TR
                            key={c.customerId}
                            className="cursor-pointer hover:bg-accent-50/40 transition-colors"
                            onClick={() => navigate(`/customers/${c.customerId}`)}
                            data-testid={`customer-row-${c.customerId}`}
                          >
                            <TD>
                              <div className="flex items-center gap-3">
                                <div className={`h-9 w-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${color}`}>
                                  {initials}
                                </div>
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-ink-900 truncate">
                                    {c.firstName} {c.lastName}
                                  </p>
                                  <p className="text-xs text-ink-400 truncate">{c.email}</p>
                                </div>
                              </div>
                            </TD>
                            <TD>
                              <span className="text-sm text-ink-600 font-mono">{c.phone || '—'}</span>
                            </TD>
                            <TD>
                              <div className="flex items-center gap-1.5">
                                <CustomerStatusBadge status={c.status} />
                                {(c.status === 'PENDING_KYC_VERIFICATION' || c.status === 'KYC_VERIFICATION_FAILED') && (
                                  <AlertTriangle
                                    className="h-3.5 w-3.5 text-red-500 shrink-0"
                                    title="Docs required"
                                    data-testid="docs-required-indicator"
                                  />
                                )}
                              </div>
                            </TD>
                            <TD>
                              <div className="min-w-0">
                                {c.referenceCode && (
                                  <p className="text-xs font-mono text-ink-700 truncate">{c.referenceCode}</p>
                                )}
                                {c.externalReference && (
                                  <p className="text-xs font-mono text-ink-400 truncate">{c.externalReference}</p>
                                )}
                                {!c.referenceCode && !c.externalReference && (
                                  <span className="text-ink-300">—</span>
                                )}
                              </div>
                            </TD>
                            <TD>
                              <span className="text-xs text-ink-500">{relativeTime(c.createdAt)}</span>
                            </TD>
                            <TD>
                              <ChevronRight className="h-4 w-4 text-ink-300" />
                            </TD>
                          </TR>
                        );
                      })}
                </TBody>
              </table>
            </div>

            {/* Empty state */}
            {!loading && customers.length === 0 && (
              <EmptyState
                icon={Users}
                title={search || statusFilter ? 'No customers match your filters' : 'No customers yet'}
                description={
                  search || statusFilter
                    ? 'Try adjusting your search or filters.'
                    : 'Customers registered through your integration will appear here.'
                }
                action={
                  activeFilterCount > 0 ? (
                    <button type="button" onClick={handleClearFilters} className="btn btn-sm btn-secondary">
                      Clear filters
                    </button>
                  ) : undefined
                }
              />
            )}

            {/* Load more */}
            {hasMore && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-ink-100">
                <p className="text-xs text-ink-400 tnum">
                  Showing {customers.length}{total != null ? ` of ${total}` : ''} customers
                </p>
                <button
                  type="button"
                  disabled={loading}
                  onClick={() => load(nextCursor)}
                  className="btn btn-sm btn-secondary"
                >
                  {loading ? (
                    <span className="h-3.5 w-3.5 rounded-full border-2 border-current border-r-transparent animate-spin" />
                  ) : (
                    <UserPlus className="h-3.5 w-3.5" />
                  )}
                  Load more
                </button>
              </div>
            )}

            {/* Count footer when all loaded */}
            {!hasMore && customers.length > 0 && (
              <div className="px-4 py-3 border-t border-ink-100 flex items-center justify-between">
                <p className="text-xs text-ink-400 tnum">
                  {customers.length} customer{customers.length !== 1 ? 's' : ''}
                  {statusFilter || search ? ' matching filters' : ' total'}
                </p>
                {(search || statusFilter) && (
                  <button
                    type="button"
                    onClick={handleClearFilters}
                    className="text-xs text-accent-600 hover:text-accent-800 font-medium"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CustomersList;
